import { useState, useMemo } from "react";
import {
  Bell,
  Settings,
  CheckCheck,
  Loader2,
  Filter,
  Inbox,
} from "lucide-react";
import { format, isToday, isYesterday, isThisWeek, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { NotificationItem, NotificationPreferences } from "@/components/notifications";
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
} from "@/hooks/use-notifications";
import type { Notification } from "@/lib/api";
import { PageMeta, DASHBOARD_PAGE_META } from "@/lib/seo";

type FilterType = "all" | "unread";

function groupNotificationsByDate(notifications: Notification[]) {
  const groups: Record<string, typeof notifications> = {};

  notifications.forEach((notification) => {
    const date = parseISO(notification.createdAt);
    let key: string;

    if (isToday(date)) {
      key = "Idag";
    } else if (isYesterday(date)) {
      key = "Igår";
    } else if (isThisWeek(date)) {
      key = format(date, "EEEE", { locale: sv });
      // Capitalize first letter
      key = key.charAt(0).toUpperCase() + key.slice(1);
    } else {
      key = format(date, "d MMMM yyyy", { locale: sv });
    }

    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(notification);
  });

  return groups;
}

export function Notifications() {
  const [filter, setFilter] = useState<FilterType>("all");
  const [showPreferences, setShowPreferences] = useState(false);
  const { data, isLoading } = useNotifications({ limit: 100 });
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteNotification = useDeleteNotification();

  const notifications = data?.notifications ?? [];

  const handleMarkAsRead = (id: string) => {
    markAsRead.mutate(id);
  };

  const handleDelete = (id: string) => {
    deleteNotification.mutate(id);
  };

  const filteredNotifications = useMemo(() => {
    if (filter === "unread") {
      return notifications.filter((n) => !n.readAt);
    }
    return notifications;
  }, [notifications, filter]);

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(filteredNotifications),
    [filteredNotifications]
  );

  const unreadCount = notifications.filter((n) => !n.readAt).length;

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  if (showPreferences) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => setShowPreferences(false)}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            &larr; Tillbaka till notifikationer
          </button>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <NotificationPreferences />
        </div>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title={DASHBOARD_PAGE_META.notifications.title}
        description={DASHBOARD_PAGE_META.notifications.description}
        noIndex
      />
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
            <Bell size={20} className="text-primary-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Notifikationer
            </h1>
            <p className="text-sm text-gray-500">
              {unreadCount > 0
                ? `${unreadCount} olästa`
                : "Alla lästa"}
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowPreferences(true)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Inställningar"
        >
          <Settings size={20} />
        </button>
      </div>

      {/* Filters & Actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setFilter("all")}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                filter === "all"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              Alla
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5",
                filter === "unread"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              <Filter size={14} />
              Olästa
              {unreadCount > 0 && (
                <span className="bg-primary-100 text-primary-700 text-xs px-1.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            disabled={markAllAsRead.isPending}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            {markAllAsRead.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <CheckCheck size={14} />
            )}
            Markera alla som lästa
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Inbox size={28} className="text-gray-400" />
            </div>
            <p className="text-gray-900 font-medium mb-1">
              {filter === "unread"
                ? "Inga olästa notifikationer"
                : "Inga notifikationer ännu"}
            </p>
            <p className="text-sm text-gray-500 text-center">
              {filter === "unread"
                ? "Du har läst alla dina notifikationer"
                : "När något händer visas det här"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {Object.entries(groupedNotifications).map(([date, items]) => (
              <div key={date}>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {date}
                  </span>
                </div>
                <div className="divide-y divide-gray-50">
                  {items.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification as Parameters<typeof NotificationItem>[0]["notification"]}
                      onMarkAsRead={handleMarkAsRead}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer info */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-400">
          Notifikationer sparas i 90 dagar.{" "}
          <button
            onClick={() => setShowPreferences(true)}
            className="text-primary-600 hover:text-primary-700"
          >
            Hantera inställningar
          </button>
        </p>
      </div>
    </div>
    </>
  );
}
